/**
 * Resolve Google Maps JavaScript API key for browser map widgets.
 *
 * Honors admin maps stack via GET /locations/maps-config:
 * - map_engine=google → return server (or env) browser key
 * - map_engine=openstreetmap → null (callers should use OSM/Leaflet)
 */

import { locationAPI } from '@/lib/api';

export type MapsSurface = 'search' | 'posting';

export type MapsRuntimeConfig = {
  mapEngine: 'google' | 'openstreetmap' | 'off';
  apiKey: string | null;
  provider: string;
  fallbackReason: string | null;
  osmFallback: boolean;
};

const cachedConfig = new Map<string, MapsRuntimeConfig>();
let viewerKey = '';

export function setMapsViewer(userId: string | null, googleMapsEnabled?: boolean): void {
  const next = `${userId ?? ''}:${googleMapsEnabled ? '1' : '0'}`;
  if (next === viewerKey) return;
  viewerKey = next;
  cachedConfig.clear();
}

export function clearMapsRuntimeConfigCache(): void {
  cachedConfig.clear();
}

function envKey(): string | null {
  const places = (import.meta.env.VITE_GOOGLE_PLACES_API_KEY as string | undefined)?.trim();
  if (places) return places;
  const browser = (import.meta.env.VITE_GOOGLE_MAPS_BROWSER_API_KEY as string | undefined)?.trim();
  if (browser) return browser;
  return null;
}

export async function resolveMapsRuntimeConfig(surface: MapsSurface = 'search'): Promise<MapsRuntimeConfig> {
  const requestViewer = viewerKey;
  const cacheId = `${requestViewer}|${surface}`;
  const cached = cachedConfig.get(cacheId);
  if (cached) return cached;
  try {
    const cfg = await locationAPI.getMapsConfig(surface);
    const wantsGoogle = cfg.map_engine === 'google';
    const apiKey = wantsGoogle ? (cfg.api_key?.trim() || envKey()) : null;
    const nextConfig: MapsRuntimeConfig = {
      mapEngine: cfg.map_engine === 'off' ? 'off' : (wantsGoogle && apiKey ? 'google' : 'openstreetmap'),
      apiKey: wantsGoogle ? apiKey : null,
      provider: cfg.provider || 'database',
      fallbackReason: wantsGoogle && apiKey ? null : (cfg.fallback_reason || null),
      osmFallback: cfg.osm_fallback !== false,
    };
    const viewerSpecific = nextConfig.fallbackReason === 'balance'
      || nextConfig.fallbackReason === 'signed_out'
      || nextConfig.fallbackReason === 'opted_out'
      || nextConfig.fallbackReason === 'guest';
    if (!viewerSpecific && requestViewer === viewerKey) {
      cachedConfig.set(cacheId, nextConfig);
    }
    return nextConfig;
  } catch {
    return {
      mapEngine: 'off',
      apiKey: null,
      provider: 'database',
      fallbackReason: null,
      osmFallback: false,
    };
  }
}

export async function resolveGoogleMapsApiKey(surface: MapsSurface = 'search'): Promise<string | null> {
  const cfg = await resolveMapsRuntimeConfig(surface);
  return cfg.mapEngine === 'google' ? cfg.apiKey : null;
}

/** Cloud Map ID for Advanced Markers; DEMO_MAP_ID works for local dev. */
export function resolveGoogleMapId(): string {
  const fromEnv = (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string | undefined)?.trim();
  return fromEnv || 'DEMO_MAP_ID';
}
